import { combine, createEvent, createStore, sample } from 'effector'

import type {
  Entry,
  FolderEntry,
} from '@/components/ui/table/expandable-dnd-table/useTreeTable.ts'
import { $activeChatDetails } from '@/models/chattingUI/chats/chats.ts'
import { $scriptCategories } from '@/models/scripts/scripts.ts'
import type { ScriptNode } from '@/modules/scripts/types.ts'
import { getFormattedMessageByMessageObject } from '@/utils/message-formatter.ts'

export const isItemValidFolder = (
  item: ScriptNode | Entry<ScriptNode>,
): item is FolderEntry<ScriptNode> => !!item.scripts

export const getItemIdentifier = (item: ScriptNode | Entry<ScriptNode>) =>
  isItemValidFolder(item) ? item.id.toString() : `${item.id}_${item.categoryId}`

export const isItemFocused = (
  item: ScriptNode | Entry<ScriptNode>,
  id: Nullable<string>,
) =>
  isItemValidFolder(item)
    ? Number(id) === item.id
    : id === `${item.id}_${item.categoryId}`

export const setScriptsFilter = createEvent<string>('set input for scripts')
export const resetScriptsFilter = createEvent('reset input for scripts')
export const $scriptsFilter = createStore('', { name: 'input for scripts' })
  .on(setScriptsFilter, (_, text) => text)
  .reset(resetScriptsFilter)

const MIN_SCRIPT_FILTER_LENGTH = 2

export const DEFAULT_SCRIPTS: Entry<ScriptNode>[] = []
export const $scripts = combine(
  $scriptsFilter,
  $scriptCategories,
  $activeChatDetails,
  (text, scripts, chat) => {
    if (!scripts.length) {
      return DEFAULT_SCRIPTS
    }

    if (!text || text.length < MIN_SCRIPT_FILTER_LENGTH) {
      return scripts
    }

    return scripts
      .map((node) => {
        const combinedResult = []

        if (node.message?.text.toLowerCase().includes(text.toLowerCase())) {
          combinedResult.push(node)
        }
        if (isItemValidFolder(node)) {
          combinedResult.push(
            ...node._listChildren().filter(({ message, name }) => {
              if (chat?.withUser) {
                const formattedText = getFormattedMessageByMessageObject(
                  message,
                  chat?.withUser,
                )

                return (
                  formattedText?.toLowerCase().includes(text.toLowerCase()) ||
                  name.toLowerCase().includes(text.toLowerCase())
                )
              }

              return false
            }),
          )

          if (node.name.toLowerCase().startsWith(text.toLowerCase())) {
            combinedResult.push(node)
          }
        }

        return combinedResult
      })
      .flat()
  },
)

export const $scriptFolderItems = $scripts.map((scripts) =>
  scripts.filter(isItemValidFolder),
)

export const $scriptItems = $scripts.map((scripts) =>
  scripts.filter((s) => !isItemValidFolder(s)),
)

export const resetFocusedScriptId = createEvent('reset focused script id')
export const setFocusedScriptId = createEvent<string>('set focused script id')
export const $focusedScriptId = createStore<Nullable<string>>(null, {
  name: 'focused script id',
})
  .on(setFocusedScriptId, (_, id) => id)
  .on($scripts, (_, scripts) =>
    scripts.length ? getItemIdentifier(scripts[0]) : null,
  )
  .reset(resetFocusedScriptId)

export const setScriptsOpen = createEvent<boolean>('set scripts open')
export const $isScriptsOpen = createStore(false, {
  name: 'are scripts open',
}).on(setScriptsOpen, (_, isOpen) => isOpen)

// open panel when have matched scripts
sample({
  clock: $scripts,
  source: $scriptsFilter,
  filter: (filter, scripts) =>
    filter.length >= MIN_SCRIPT_FILTER_LENGTH && scripts.length > 0,
  fn: () => true,
  target: setScriptsOpen,
})
// close panel when no scripts found
sample({
  clock: $scripts,
  source: $scriptsFilter,
  filter: (filter, scripts) =>
    filter.length >= MIN_SCRIPT_FILTER_LENGTH && scripts.length === 0,
  fn: () => false,
  target: setScriptsOpen,
})
sample({
  clock: $activeChatDetails,
  filter: (chat) => !chat,
  fn: () => false,
  target: setScriptsOpen,
})
